/**
 * Auth0 API audience. Must match `authorizationParams.audience` in `auth0Provider.js`.
 * Australia and New Zealand use the same tenant, client, and audience.
 */
export const AUTH0_API_AUDIENCE = "https://priorityone.au.auth0.com/api/v2/";

export const SESSION_TOKEN_KEY = "token";

/** Ignore values that are not a real access token (including the string "undefined"). */
export function normalizeAccessToken(token) {
  const value = String(token ?? "").trim();
  if (!value || value === "undefined" || value === "null") return "";
  return value;
}

export function readSessionToken() {
  if (typeof sessionStorage === "undefined") return "";
  return normalizeAccessToken(sessionStorage.getItem(SESSION_TOKEN_KEY));
}

export function writeSessionToken(token) {
  const value = normalizeAccessToken(token);
  if (!value) return false;
  sessionStorage.setItem(SESSION_TOKEN_KEY, value);
  return true;
}

export function clearSessionToken() {
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
}

/**
 * Audience-scoped access token, then the Auth0Provider defaults.
 * Same sequence as `useRefreshSessionToken`.
 * @returns {Promise<string>} trimmed token, or "" when Auth0 returns nothing
 */
export async function fetchApiAccessToken(getAccessTokenSilently) {
  try {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: AUTH0_API_AUDIENCE,
      },
    });
    const value = normalizeAccessToken(token);
    if (value) return value;
  } catch (_audienceError) {
    // Provider defaults already include the audience; retry once before giving up.
  }
  const token = await getAccessTokenSilently();
  return normalizeAccessToken(token);
}

/** Paths that stay usable with no Auth0 session. The marketing site embeds these. */
const PUBLIC_UNAUTHENTICATED_PATHS = new Set([
  "/current-wash-locations-map",
  "/activate",
  "/signup",
  "/success",
  "/cancel",
]);

export function normalizeAppPath(pathname = "") {
  const path = String(pathname || "").split("?")[0].replace(/\/+$/, "") || "/";
  return path;
}

/**
 * Silent-auth failure (`missing`) sends the member app to Auth0.
 * The location map, signup, and activation pages are public — including when
 * the marketing site embeds them — and must not be replaced by the login form.
 */
export function isPublicAppPath(pathname = "") {
  return PUBLIC_UNAUTHENTICATED_PATHS.has(normalizeAppPath(pathname));
}

export function shouldRedirectToLogin({ result, pathname, embedded = false } = {}) {
  if (result !== "missing") return false;
  if (embedded) return false;
  return !isPublicAppPath(pathname);
}

/**
 * Keep `sessionStorage.token` aligned with Auth0.
 *
 * While `isLoading` is true, `user` is missing even for a signed-in session.
 * The AU|NZ switch is a full navigation, so this origin's sessionStorage starts
 * empty — and a same-origin refresh can still hold a token that must not be
 * deleted before Auth0 finishes restoring the user.
 *
 * @returns {Promise<"pending"|"stored"|"missing"|"signed_out">}
 * `missing` means silent auth failed. The shell may `loginWithRedirect()` only
 * when `shouldRedirectToLogin` is true.
 */
export async function syncSessionAccessToken({ isLoading, user, getAccessTokenSilently }) {
  if (isLoading) {
    return "pending";
  }
  if (!user) {
    clearSessionToken();
    return "signed_out";
  }
  try {
    const token = await fetchApiAccessToken(getAccessTokenSilently);
    if (writeSessionToken(token)) {
      return "stored";
    }
    return "missing";
  } catch (error) {
    console.error("Error getting Auth0 token:", error);
    return "missing";
  }
}
